import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { CallState, TwilioDeviceState, CallEntityType } from '@/types/twilio';
import { useApiClient } from '@api-client/context';
import { useStore } from '@hooks/useStore';
import axios from 'axios';

const TWILIO_MICROPHONE_PERMISSION_ERROR_CODE = 31401;

const isMicrophonePermissionError = (error: any): boolean => {
  // Check for microphone permission error (Twilio error code 31401)
  // Handle both string and number error codes, and PermissionDeniedError type
  const errorCode = error?.code;
  const errorName = error?.name || error?.constructor?.name || '';
  const errorMessage = error?.message || '';

  return (
    errorCode === TWILIO_MICROPHONE_PERMISSION_ERROR_CODE ||
    Number(errorCode) === TWILIO_MICROPHONE_PERMISSION_ERROR_CODE ||
    errorMessage.includes('(31401)')
  );
};

const getErrorMessage = (error: any): string => {
  const isPermissionError = isMicrophonePermissionError(error);

  if (isPermissionError) {
    return 'You need to give your browser permission to use your microphone. Please contact support if you need help on how to do this.';
  }

  return error?.message || 'An error occurred';
};

export function useTwilioVoice() {
  const api = useApiClient();
  const { authStore } = useStore();
  const { user } = authStore;
  const deviceRef = useRef<Device | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [deviceState, setDeviceState] = useState<TwilioDeviceState>({
    isRegistered: false,
    isInitializing: false,
    error: null
  });

  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    phoneNumber: null,
    phoneCallId: null,
    callSid: null,
    duration: 0,
    error: null,
    entityId: null,
    entityType: null
  });

  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    let seconds = 0;
    durationIntervalRef.current = setInterval(() => {
      seconds++;
      setCallState((prev) => ({ ...prev, duration: seconds }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const destroyDevice = useCallback(async () => {
    if (deviceRef.current) {
      try {
        await deviceRef.current.unregister();
        deviceRef.current.destroy();
        deviceRef.current = null;
        setDeviceState({
          isRegistered: false,
          isInitializing: false,
          error: null
        });
      } catch (error: any) {}
    }
  }, []);

  const initializeDevice = useCallback(
    async (token: string) => {
      if (deviceState.isInitializing) {
        return;
      }

      // Destroy existing device before creating a new one
      if (deviceRef.current) {
        await destroyDevice();
      }

      setDeviceState((prev) => ({ ...prev, isInitializing: true }));

      try {
        const device = new Device(token, {
          logLevel: 1,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU]
        });

        device.on('registered', () => {
          setDeviceState({
            isRegistered: true,
            isInitializing: false,
            error: null
          });
        });

        device.on('error', (error) => {
          const errorMessage = getErrorMessage(error);
          setDeviceState((prev) => ({
            ...prev,
            isInitializing: false,
            error: errorMessage
          }));
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: errorMessage
          }));
        });

        device.on('tokenWillExpire', () => {
          console.warn(
            'Twilio token will expire soon. Device will be refreshed on next call.'
          );
        });

        await device.register();
        deviceRef.current = device;
      } catch (error: any) {
        console.error('Failed to initialize Twilio Device:', error);
        const errorMessage = getErrorMessage(error);
        setDeviceState({
          isRegistered: false,
          isInitializing: false,
          error: errorMessage
        });
      }
    },
    [deviceState.isInitializing, destroyDevice]
  );

  const initiateCall = useCallback(
    async (
      entityId: string,
      phoneNumber: string,
      callerName: string,
      entityType: CallEntityType
    ) => {
      // Check if user has permission to make phone calls
      if (!user?.canMakePhoneCalls) {
        setCallState((prev) => ({
          ...prev,
          status: 'error',
          error: 'You do not have permission to make phone calls'
        }));
        return;
      }

      try {
        setCallState({
          status: 'initiating',
          phoneNumber,
          phoneCallId: null,
          callSid: null,
          duration: 0,
          error: null,
          entityId,
          entityType,
          callerName: callerName || user?.name
        });

        const callResponse = await api.twilio.initiateCall(
          entityId,
          entityType
        );

        // Always initialize device with fresh token for each call
        await initializeDevice(callResponse?.data?.access_token);

        if (!deviceRef.current) {
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: 'Device not initialized'
          }));
          return;
        }

        setCallState((prev) => ({
          ...prev,
          status: 'connecting',
          phoneCallId: callResponse?.data?.phone_call?.id?.toString() || null,
          callerName: callResponse?.data?.phone_call?.name || null
        }));

        const call = await deviceRef.current.connect({
          params: {
            To: callResponse?.data?.phone_call?.to || ''
          }
        });

        currentCallRef.current = call;

        call.on('accept', () => {
          setCallState((prev) => ({ ...prev, status: 'ringing' }));
        });

        call.on('connect', () => {
          setCallState((prev) => ({ ...prev, status: 'connected' }));
          startDurationTimer();
        });

        call.on('disconnect', (event) => {
          stopDurationTimer();

          // Clear any existing timeout
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
          }

          setCallState({
            status: 'disconnected',
            phoneNumber: null,
            phoneCallId: null,
            callSid: null,
            duration: 0,
            error: null,
            entityId: null,
            entityType: null
          });
          currentCallRef.current = null;

          disconnectTimeoutRef.current = setTimeout(() => {
            disconnectTimeoutRef.current = null;
            destroyDevice();
            setCallState((prev) => ({ ...prev, status: 'idle' }));
          }, 4500);
        });

        call.on('error', (error) => {
          console.error('Call Error:', error, callState);

          // Clear the disconnect timeout so it doesn't overwrite the error state
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
          }

          function setBrowserPermissionError() {
            setCallState((prev) => ({
              ...prev,
              status: 'browserPermissionError',
              error:
                'You need to give your browser permission to use your microphone. Please contact support if you need help on how to do this.'
            }));
          }

          if (isMicrophonePermissionError(error)) {
            setBrowserPermissionError();
            setTimeout(() => {
              setBrowserPermissionError();
            }, 5000);
            return;
          }
          stopDurationTimer();

          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: error.message || 'An error occurred'
          }));
        });

        call.on('reject', () => {
          stopDurationTimer();
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: 'Call was rejected'
          }));
        });

        call.on('cancel', () => {
          stopDurationTimer();
          setCallState({
            status: 'disconnected',
            phoneNumber: null,
            phoneCallId: null,
            callSid: null,
            duration: 0,
            error: null,
            entityId: null,
            entityType: null
          });
          setTimeout(() => {
            setCallState((prev) => ({ ...prev, status: 'idle' }));
          }, 1000);
        });
      } catch (error: any) {
        console.error('Axios error:', error);
        if (axios.isAxiosError(error)) {
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: error.response?.data?.error || 'Failed to initiate call'
          }));
        }
      }
    },
    [
      api,
      initializeDevice,
      startDurationTimer,
      stopDurationTimer,
      user?.canMakePhoneCalls,
      user?.name
    ]
  );

  const terminateCall = useCallback(() => {
    console.log('terminateCall');

    // Clear disconnect timeout if it exists
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }

    if (!currentCallRef.current) {
      console.warn('No active call to terminate');
      setCallState((prev) => ({
        ...prev,
        status: 'idle',
        phoneNumber: null,
        phoneCallId: null,
        callSid: null,
        duration: 0,
        error: null,
        entityId: null,
        entityType: null
      }));
      return;
    }

    try {
      // Disconnect the call on the frontend
      currentCallRef.current.disconnect();

      stopDurationTimer();
      setCallState({
        status: 'disconnected',
        phoneNumber: null,
        phoneCallId: null,
        callSid: null,
        duration: 0,
        error: null,
        entityId: null,
        entityType: null
      });

      currentCallRef.current = null;

      setTimeout(() => {
        setCallState((prev) => ({ ...prev, status: 'idle' }));
      }, 2000);
    } catch (error: any) {
      console.error('Failed to terminate call:', error);
      setCallState((prev) => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to terminate call'
      }));
    }
  }, [stopDurationTimer]);

  const getCallStatusText = useCallback((): string => {
    switch (callState.status) {
      case 'initiating':
        return 'Initiating...';
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Call ended';
      case 'error':
        return callState.error || 'Error occurred';
      default:
        return '';
    }
  }, [callState.status, callState.error]);

  useEffect(() => {
    return () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      if (currentCallRef.current) {
        currentCallRef.current.disconnect();
      }
      destroyDevice();
      stopDurationTimer();
    };
  }, [stopDurationTimer, destroyDevice]);

  return {
    deviceState,
    callState,
    initiateCall,
    terminateCall,
    getCallStatusText,
    initializeDevice
  };
}
